'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  allowanceLabel,
  fetchRevealAllowance,
  revealBuyerRequestContact,
  revealListingContact,
  type RevealAllowance,
  type RevealResult,
} from '@/lib/reveals';

/**
 * "Reveal contact details" — used on a listing (reveals the seller) and on a
 * wanted ad (reveals the buyer). Same component, same allowance.
 */
export default function RevealContactPanel({
  listingId,
  buyerRequestId,
  ownerName,
  compact = false,
}: {
  listingId?: string;
  buyerRequestId?: string;
  ownerName: string;
  compact?: boolean;
}) {
  const { user, loading: authLoading } = useAuth();
  const supabase = useMemo(() => createClient(), []);

  const [allowance, setAllowance] = useState<RevealAllowance | null>(null);
  const [result, setResult] = useState<RevealResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading || !user) return;
    let active = true;
    fetchRevealAllowance(supabase).then((a) => {
      if (active) setAllowance(a);
    });
    return () => {
      active = false;
    };
  }, [supabase, user, authLoading]);

  const reveal = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = listingId
        ? await revealListingContact(supabase, listingId)
        : await revealBuyerRequestContact(supabase, buyerRequestId!);
      setResult(res);
      setAllowance(await fetchRevealAllowance(supabase));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not fetch the contact details.');
    } finally {
      setBusy(false);
    }
  };

  // ── Signed out ──────────────────────────────────────────────────────────────
  if (!authLoading && !user) {
    return (
      <div className={compact ? '' : 'card'}>
        <h2 className="text-sm font-bold text-foreground uppercase tracking-wider mb-2">
          Contact details
        </h2>
        <p className="text-xs text-muted-foreground leading-relaxed mb-3">
          Members can see {ownerName}&apos;s phone number. Registering is free and includes 4
          contact reveals a year.
        </p>
        <Link href="/sign-up-login-screen" className="btn-primary w-full justify-center text-sm py-2.5">
          <Icon name="UserPlusIcon" size={16} />
          Sign in to reveal
        </Link>
      </div>
    );
  }

  // ── Revealed ────────────────────────────────────────────────────────────────
  if (result?.revealed) {
    return (
      <div className={compact ? 'border-t border-border pt-3' : 'card bg-green-50 border-green-200'}>
        <h2 className="text-sm font-bold text-foreground uppercase tracking-wider mb-2">
          Contact details
        </h2>
        <p className="text-sm font-bold text-foreground">{result.name || ownerName}</p>
        {result.phone ? (
          <a
            href={`tel:${result.phone.replace(/\s+/g, '')}`}
            className="flex items-center gap-2 text-primary font-bold text-lg mt-1 hover:underline"
          >
            <Icon name="PhoneIcon" size={18} />
            {result.phone}
          </a>
        ) : (
          <p className="text-xs text-muted-foreground mt-1">
            No phone number on file — send them a message instead.
          </p>
        )}
        {result.email && (
          <p className="text-xs text-muted-foreground mt-1 break-all">{result.email}</p>
        )}
        <p className="text-[11px] text-muted-foreground mt-3">
          {result.alreadyRevealed
            ? 'You had already revealed this one, so it did not use another credit.'
            : 'One contact reveal used.'}
          {allowance && allowance.limit !== null && ` ${allowanceLabel(allowance)}.`}
        </p>
      </div>
    );
  }

  // ── Allowance used up ───────────────────────────────────────────────────────
  if (result && !result.revealed && result.reason === 'limit') {
    return (
      <div className={compact ? 'border-t border-border pt-3' : 'card border-amber-200 bg-amber-50'}>
        <h2 className="text-sm font-bold text-amber-900 uppercase tracking-wider mb-2">
          Reveal limit reached
        </h2>
        <p className="text-xs text-amber-900 leading-relaxed mb-3">
          You have used all {result.limit} contact reveals on your{' '}
          {result.tier === 'free' ? 'free' : result.tier} membership this year. Upgrade for more,
          or message {ownerName} through the site — that is always free.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Link href="/membership-pricing" className="btn-primary text-sm py-2 justify-center">
            <Icon name="ArrowUpCircleIcon" size={16} />
            See plans
          </Link>
        </div>
      </div>
    );
  }

  // ── Ready to reveal ─────────────────────────────────────────────────────────
  const outOfCredits =
    allowance != null && allowance.limit !== null && (allowance.remaining ?? 0) <= 0;

  return (
    <div className={compact ? 'border-t border-border pt-3' : 'card'}>
      <h2 className="text-sm font-bold text-foreground uppercase tracking-wider mb-2">
        Contact details
      </h2>
      <p className="text-xs text-muted-foreground leading-relaxed mb-3">
        {ownerName}&apos;s phone number is hidden. Revealing it uses one of your contact reveals.
      </p>
      <button
        type="button"
        onClick={reveal}
        disabled={busy || outOfCredits}
        className="btn-primary w-full justify-center text-sm py-2.5 disabled:opacity-60"
      >
        <Icon name={busy ? 'ArrowPathIcon' : 'EyeIcon'} size={16} className={busy ? 'animate-spin' : undefined} />
        {busy ? 'Revealing…' : outOfCredits ? 'No reveals left' : 'Reveal contact number'}
      </button>
      {error && <p className="text-xs text-red-600 mt-2" role="alert">{error}</p>}
      {allowance && (
        <p className="text-[11px] text-muted-foreground mt-2.5">
          {allowanceLabel(allowance)}
          {outOfCredits && (
            <>
              {' — '}
              <Link href="/membership-pricing" className="underline font-semibold">
                upgrade for more
              </Link>
            </>
          )}
        </p>
      )}
    </div>
  );
}
